import { AiSolutionSuccess, IChatMessage } from '@cont/DoubtsUnacademyAI/types';
import {
  createContext,
  Dispatch,
  FC,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

export type FlowType =
  | 'input'
  | 'questionOptions'
  | 'limit'
  | 'loading'
  | 'error';

interface GoalInfo {
  goalUid: string;
  goalSlug: string;
}

export interface IDoubtsAIContext {
  doubtsUID: string;
  setDoubtsUID: Dispatch<SetStateAction<string>>;
  submittedImageUrl: string;
  setSubmittedImageUrl: Dispatch<SetStateAction<string>>;
  chats: IChatMessage[];
  addMessage: (message: IChatMessage) => void;
  flowType: FlowType;
  setFlowType: (flowType: FlowType) => void;
  clearChat: () => void;
  isGoingBack: boolean;
  setIsGoingBack: (isGoingBack: boolean) => void;
  isAskNewQuestion: boolean;
  setIsAskNewQuestion: (isAskNewQuestion: boolean) => void;
  wrapperContainerRef: MutableRefObject<HTMLDivElement>;
  scrollToBottom: () => void;
  imageAiResponse: AiSolutionSuccess;
  setImageAiResponse: Dispatch<SetStateAction<AiSolutionSuccess>>;
  limitReachedData: AiSolutionSuccess['sub_cta_details'];
  setLimitReachedData: Dispatch<
    SetStateAction<AiSolutionSuccess['sub_cta_details']>
  >;
  goalInfo: GoalInfo;
  setGoalInfo: Dispatch<SetStateAction<GoalInfo>>;
  doubtNumber: number;
  setDoubtNumber: Dispatch<SetStateAction<number>>;
}

const emptyFn = () => {};

export const DoubtsAIContext = createContext<IDoubtsAIContext>({
  doubtsUID: undefined,
  setDoubtsUID: emptyFn,
  submittedImageUrl: '',
  setSubmittedImageUrl: emptyFn,
  chats: [],
  addMessage: emptyFn,
  flowType: 'input',
  setFlowType: emptyFn,
  clearChat: emptyFn,
  isGoingBack: false,
  setIsGoingBack: emptyFn,
  isAskNewQuestion: false,
  setIsAskNewQuestion: emptyFn,
  wrapperContainerRef: null,
  scrollToBottom: emptyFn,
  imageAiResponse: null,
  setImageAiResponse: emptyFn,
  limitReachedData: null,
  setLimitReachedData: emptyFn,
  goalInfo: null,
  setGoalInfo: emptyFn,
  doubtNumber: 0,
  setDoubtNumber: emptyFn
});

export const DoubtsAIContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [doubtsUID, setDoubtsUID] = useState();
  const [submittedImageUrl, setSubmittedImageUrl] = useState(null);
  const [chats, setChats] = useState<IChatMessage[]>([]);
  const [flowType, setFlowType] = useState<FlowType>('input');
  const [isGoingBack, setIsGoingBack] = useState(false);
  const [isAskNewQuestion, setIsAskNewQuestion] = useState(false);
  const [imageAiResponse, setImageAiResponse] =
    useState<AiSolutionSuccess>(null);
  const [limitReachedData, setLimitReachedData] =
    useState<AiSolutionSuccess['sub_cta_details']>();
  const [doubtNumber, setDoubtNumber] = useState<number>();

  const [goalInfo, setGoalInfo] = useState<GoalInfo>();

  const wrapperContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    const scrollElement = wrapperContainerRef.current;
    if (scrollElement) {
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  }, []);

  const addMessage = useCallback((message: IChatMessage) => {
    setChats((prevChats) => [...prevChats, message]);
  }, []);

  const clearChat = useCallback(() => {
    setChats([]);
    setFlowType('loading');
    window.location.reload(); // TODO: Currently reloading but fix this later
  }, []);

  const data = {
    doubtsUID,
    setDoubtsUID,
    submittedImageUrl,
    setSubmittedImageUrl,
    chats,
    addMessage,
    flowType,
    setFlowType,
    clearChat,
    isGoingBack,
    setIsGoingBack,
    isAskNewQuestion,
    setIsAskNewQuestion,
    wrapperContainerRef,
    scrollToBottom,
    imageAiResponse,
    setImageAiResponse,
    limitReachedData,
    setLimitReachedData,
    goalInfo,
    setGoalInfo,
    doubtNumber,
    setDoubtNumber
  };

  return (
    <DoubtsAIContext.Provider value={data}>{children}</DoubtsAIContext.Provider>
  );
};
